import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  getWeeklyRevenueData,
  getDashboardStats,
  getRecentOrders,
  getMonthlyRevenueData,
  getOrderAnalyticsData,
} from "../services/stores/mockData";

export function useDashboardData(storeId: string) {
  const [weeklyRevenueData, setWeeklyRevenueData] = useState([]);
  const [stats, setStats] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [monthlyRevenueData, setMonthlyRevenueData] = useState([]);
  const [orderAnalyticsData, setOrderAnalyticsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch dashboard data when storeId changes
  useEffect(() => {
    if (!storeId) {
      return;
    }

    const loadData = async () => {
      setIsLoading(true);
      try {
        // Fetch all data in parallel
        const [
          weeklyRevenue,
          dashboardStats,
          recent,
          monthlyRevenue,
          orderAnalytics,
        ] = await Promise.all([
          getWeeklyRevenueData(storeId),
          getDashboardStats(storeId),
          getRecentOrders(storeId),
          getMonthlyRevenueData(storeId),
          getOrderAnalyticsData(storeId),
        ]);

        setWeeklyRevenueData(weeklyRevenue);
        setStats(dashboardStats);
        setRecentOrders(recent);
        setMonthlyRevenueData(monthlyRevenue);
        setOrderAnalyticsData(orderAnalytics);
      } catch (error) {
        console.error("Error loading dashboard data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();

    // Set up real-time subscription for store data updates
    const ordersChannel = supabase
      .channel(`dashboard-orders-${storeId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "orders",
          filter: `store_id=eq.${storeId}`,
        },
        async () => {
          // Refresh data that depends on orders
          const [dashboardStats, recent, orderAnalytics] = await Promise.all([
            getDashboardStats(storeId),
            getRecentOrders(storeId),
            getOrderAnalyticsData(storeId),
          ]);

          setStats(dashboardStats);
          setRecentOrders(recent);
          setOrderAnalyticsData(orderAnalytics);
        }
      )
      .subscribe();

    const salesChannel = supabase
      .channel(`dashboard-sales-${storeId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "store_sales",
          filter: `store_id=eq.${storeId}`,
        },
        async () => {
          // Refresh revenue data
          const [weeklyRevenue, monthlyRevenue] = await Promise.all([
            getWeeklyRevenueData(storeId),
            getMonthlyRevenueData(storeId),
          ]);

          setWeeklyRevenueData(weeklyRevenue);
          setMonthlyRevenueData(monthlyRevenue);
        }
      )
      .subscribe();

    // Return cleanup function
    return () => {
      supabase.removeChannel(ordersChannel);
      supabase.removeChannel(salesChannel);
    };
  }, [storeId]);

  return {
    weeklyRevenueData,
    stats,
    recentOrders,
    monthlyRevenueData,
    orderAnalyticsData,
    isLoading,
  };
}
